import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Square, SquareCheck, X } from 'lucide-react';

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectDropdownProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** When true, renders a search input above the option list. Useful for free-text values like family groups. */
  searchable?: boolean;
  /** When true, displays a `(multi)` hint inside the placeholder when no value is selected. */
  showMultiHint?: boolean;
  'data-testid'?: string;
}

/**
 * Generic dropdown that allows selecting multiple values from a static list of
 * options. Each option renders an explicit checkbox so the multi-select
 * affordance is always visible. Use for filter bars (RSVP, side, family
 * group, etc.) anywhere we need a small enum-style multi-select. For entity
 * selection (vendor, contact, venue) prefer SearchableSelect with `multiple`.
 */
export function MultiSelectDropdown({
  options,
  value,
  onChange,
  placeholder = 'All',
  disabled,
  className,
  searchable = false,
  showMultiHint = true,
  'data-testid': testId,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Reset the search field when the menu closes so it doesn't re-open with stale input.
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const selectedSet = useMemo(() => new Set(value), [value]);
  const selectedOptions = options.filter((o) => selectedSet.has(o.value));

  const filteredOptions = useMemo(() => {
    if (!searchable || !search.trim()) return options;
    const term = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(term));
  }, [options, search, searchable]);

  function toggle(optionValue: string) {
    const next = selectedSet.has(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange(next);
  }

  function clearAll(e: React.MouseEvent) {
    e.stopPropagation();
    onChange([]);
  }

  const hint = showMultiHint ? ' (multi)' : '';
  const buttonLabel =
    selectedOptions.length === 0
      ? `${placeholder}${hint}`
      : selectedOptions.length === 1
        ? selectedOptions[0].label
        : `${selectedOptions.length} selected`;

  return (
    <div ref={containerRef} className={`relative ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        data-testid={testId}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span
          className={
            selectedOptions.length === 0
              ? 'text-gray-500 truncate text-left flex-1'
              : 'text-gray-900 truncate text-left flex-1'
          }
        >
          {buttonLabel}
        </span>
        <span className="flex items-center gap-1">
          {selectedOptions.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              aria-label="Clear selection"
              className="text-gray-400 hover:text-gray-600 p-0.5"
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown size={14} className="text-gray-500" />
        </span>
      </button>

      {/* Selected chips, shown below the trigger when 2+ values are selected so users can see/remove individual picks. */}
      {selectedOptions.length >= 2 && (
        <div
          className="mt-1 flex flex-wrap gap-1"
          data-testid={testId ? `${testId}-chips` : undefined}
        >
          {selectedOptions.map((opt) => (
            <span
              key={opt.value}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 text-xs"
            >
              {opt.label}
              <button
                type="button"
                onClick={() => toggle(opt.value)}
                aria-label={`Remove ${opt.label}`}
                className="hover:text-primary-900"
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-72 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg">
          {searchable && (
            <div className="sticky top-0 bg-white p-2 border-b border-gray-100">
              <input
                type="text"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                aria-label="Search options"
                data-testid={testId ? `${testId}-search` : undefined}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          )}
          {filteredOptions.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">
              {options.length === 0 ? 'No options' : 'No matches'}
            </div>
          )}
          {filteredOptions.map((opt) => {
            const selected = selectedSet.has(opt.value);
            const Icon = selected ? SquareCheck : Square;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggle(opt.value)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 ${
                  selected ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                }`}
                data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
              >
                <Icon
                  size={16}
                  className={selected ? 'text-primary-600' : 'text-gray-400'}
                  aria-hidden="true"
                />
                <span className="truncate flex-1">{opt.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
